function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer, Container } from "../../components/container";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader, Switcher, Ribbon, Card, Search, Filters, Pill, Empty } from "../../components/analytics-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import { getOnboardingStatus } from "../../functions/onboarding-status";
import { emailSvc, delayLabel, triggerLabel, starterNote, recipientFunnel, num } from "./common";

// The Email section's views (the switcher under the header). Campaigns is the
// first tab and the landing when the nav says "Email"; /EmailAutomation itself
// keeps rendering Automations so old links work (BULK_EMAIL_MANAGEMENT §3).
// 2026-10-04: single emails (automations) are listed under Journeys, so the
// Automations tab is gone; the /Email/automations routes still work and light
// the Journeys tab.
export const EMAIL_VIEWS = [{
  to: "/Email/campaigns",
  label: "Campaigns",
  isActive: p => /^\/(Email|EmailAutomation)\/campaigns/i.test(p) || /^\/Email\/?$/i.test(p)
}, {
  to: "/Email/journeys",
  label: "Journeys",
  isActive: p => /^\/(Email|EmailAutomation)\/journeys/i.test(p) || /^\/Email\/automations/i.test(p) || /^\/EmailAutomation\/?$/i.test(p) || /^\/EmailAutomation\/(new|edit|detail)/i.test(p)
}, {
  to: "/Email/settings",
  label: "Settings",
  isActive: p => /^\/(Email|EmailAutomation)\/(settings|senders)/i.test(p)
}];
// Outreach (OUTREACH_2026-10-02 mockup 1): its own tab for workspaces with the `outreach` registry flag (DS 2026-10-07
// "Both": the tab plus the Outreach kind in New campaign). Resolved per render because flags arrive with the session.
const OUTREACH_VIEW = {
  to: "/Email/outreach",
  label: "Outreach",
  isActive: p => /^\/Email\/outreach/i.test(p)
};
export function emailViews() {
  if (!hasTenantFeature("outreach")) return EMAIL_VIEWS;
  return [EMAIL_VIEWS[0], EMAIL_VIEWS[1], OUTREACH_VIEW, EMAIL_VIEWS[2]];
}

/*
 * Email Automation dashboard (spec §4). KPI strip Triggered · Waiting ·
 * Sent comes from recipient rows (real data today); opens/clicks land
 * when the delivery webhook loop closes. Draft starters render as an
 * onboarding gallery — with the two honesty labels the spec requires.
 */

class EmailAutomationViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "open", id => () => this.props.history.push(`/Email/automations/detail/${id}`));
    this.state = {
      automations: null,
      funnel: {
        entered: 0,
        waiting: 0,
        sent: 0
      },
      totals: null,
      error: false
    };
    this._alive = true;
  }
  async componentDidMount() {
    getOnboardingStatus().then(ob => this.setState({
      shopify: !!(ob && ob.shopify)
    })).catch(() => {});
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    try {
      const d = await emailSvc("/automations?range=DAY").getAsync();
      if (!this._alive) return;
      const automations = d && d.automations || [];
      this.setState({
        automations,
        totals: d && d.totalMetrics || null
      });
      // Recipient rollup — one call per automation; fine at starter
      // scale, capped to keep the dashboard cheap if the registry grows.
      const withRecipients = automations.slice(0, 12);
      const lists = await Promise.all(withRecipients.map(a => emailSvc(`/automations/${a.id}/recipients`).getAsync().catch(() => [])));
      if (!this._alive) return;
      const all = [].concat(...lists.map(l => l || []));
      const funnel = recipientFunnel(all);
      const perAuto = {};
      withRecipients.forEach((a, i) => {
        perAuto[a.id] = recipientFunnel(lists[i] || []);
      });
      this.setState({
        funnel,
        perAuto
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Email Automation isn't configured for this workspace: contact Plunk to enable it."));
    }
    const {
      automations,
      funnel,
      error,
      perAuto
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: "Email automations could not be loaded."
    });
    if (!automations) return /*#__PURE__*/React.createElement(Loader, null);
    const active = automations.filter(a => a.status !== "Draft");
    // Shopify workspaces get their automations as Journeys ("Bring your email over"); the
    // legacy starter drafts would show Cart Recovery twice, so they hide there (2026-09-27).
    const hideStarters = !!this.state.shopify;
    const isStarter = a => starterNote(a) || ["ProductViewed", "CartAbandoned", "WelcomeSeries", "RepeatCustomer"].includes(String(a.triggerType || "")) || /^(Welcome Series|Cart Recovery|Products Viewed Re-engagement|Customer Loyalty)$/i.test(String(a.name || ""));
    const drafts = automations.filter(a => a.status === "Draft" && !(hideStarters && isStarter(a)));
    const row = a => {
      const m = a.metrics || {};
      const f = (perAuto || {})[a.id];
      const note = starterNote(a);
      return /*#__PURE__*/React.createElement("tr", {
        key: a.id,
        className: "clickable",
        onClick: this.open(a.id),
        title: "Open automation"
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, a.name), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, triggerLabel(a), " \xB7 ", delayLabel(a.scheduleConfig))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: statusChipStyle(a.status)
      }, String(a.status || "").toLowerCase()), note && note.comingSoon ? /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: {
          background: "var(--plk-surface-alt)",
          color: "var(--plk-text-dim)",
          marginLeft: "4px"
        }
      }, "coming soon") : null), /*#__PURE__*/React.createElement("td", {
        className: "r"
      }, f ? num(f.entered) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "r"
      }, f ? num(f.waiting) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "r"
      }, num(m.sent)), /*#__PURE__*/React.createElement("td", {
        className: "r"
      }, Number(m.uniqueOpens) > 0 ? num(m.uniqueOpens) : "—"));
    };
    const tone = st => ({
      running: "success",
      paused: "warning",
      draft: "neutral",
      archived: "neutral",
      failed: "danger"
    })[String(st || "").toLowerCase()] || "neutral";
    const q = (this.state.q || "").toLowerCase();
    const match = a => !q || `${a.name} ${triggerLabel(a)}`.toLowerCase().includes(q);
    const rowK = a => {
      const f = this.state.perAuto && this.state.perAuto[a.id];
      const m = a.metrics || {};
      const note = starterNote(a);
      return /*#__PURE__*/React.createElement("tr", {
        key: a.id,
        className: "clickable",
        onClick: this.open(a.id),
        title: "Open automation"
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, a.name), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, triggerLabel(a), " \xB7 ", delayLabel(a.scheduleConfig))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: tone(a.status)
      }, String(a.status || "").toLowerCase()), note && note.comingSoon ? /*#__PURE__*/React.createElement(Pill, {
        tone: "info"
      }, "coming soon") : null), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, f ? num(f.entered) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, f ? num(f.waiting) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(m.sent)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, Number(m.uniqueOpens) > 0 ? num(m.uniqueOpens) : "—"));
    };
    const table = rows => /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Automation"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Triggered"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Waiting"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sent"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Opens"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(rowK))));
    const running = active.filter(a => String(a.status).toLowerCase() === "running").length;
    const totalSent = automations.reduce((t, a) => t + (Number((a.metrics || {}).sent) || 0), 0);
    const totalOpens = automations.reduce((t, a) => t + (Number((a.metrics || {}).uniqueOpens) || 0), 0);
    const activeRows = active.filter(match),
      draftRows = drafts.filter(match);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Single emails",
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Email/journeys"
      }, "Journeys"), " \xB7 Single emails"),
      subtitle: `${num(active.length)} active single email${active.length === 1 ? "" : "s"} · ${num(drafts.length)} draft${drafts.length === 1 ? "" : "s"}${hideStarters ? "" : " and starters"} · one triggered email, sent once per contact · also listed under Journeys`,
      chips: [{
        k: "Running",
        v: num(running)
      }],
      actions: /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        onClick: () => this.props.history.push("/Email/automations/new")
      }, "New single email")
    }), /*#__PURE__*/React.createElement(Switcher, {
      items: emailViews(),
      ariaLabel: "Email views"
    }), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: [{
        l: "Triggered",
        v: num(funnel.entered),
        s: "contacts that entered an automation"
      }, {
        l: "Waiting on delay",
        v: num(funnel.waiting),
        s: "queued for a scheduled send"
      }, {
        l: "Sent",
        v: num(funnel.sent),
        s: totalSent ? `${num(totalSent)} on the delivery log` : "per the recipient log"
      }, {
        l: "Opened",
        v: totalOpens > 0 ? num(totalOpens) : "—",
        s: totalOpens > 0 ? `${totalSent ? Math.round(totalOpens / totalSent * 100) : 0}% of sent` : "arrives with delivery tracking"
      }]
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Automations",
      hint: `${num(activeRows.length)} active`,
      right: /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement(Search, {
        value: this.state.q || "",
        onChange: v => this.setState({
          q: v
        }),
        placeholder: "Search name or trigger"
      }))
    }, activeRows.length ? table(activeRows) : /*#__PURE__*/React.createElement(Empty, null, active.length ? "No active automation matches." : "No active automations yet: activate a draft or starter below, or create one for a one-off triggered email.")), drafts.length > 0 ? /*#__PURE__*/React.createElement(Card, {
      title: hideStarters ? "Drafts" : active.length ? "Drafts and starters" : "Starters ready to configure",
      hint: hideStarters ? "from the Content Hub" : "open one, set the sender and content, then activate"
    }, draftRows.length ? table(draftRows) : /*#__PURE__*/React.createElement(Empty, null, "No draft matches.")) : null, hideStarters && !active.length ? /*#__PURE__*/React.createElement(Card, {
      title: "Shopify Email keeps the basics",
      hint: "welcome, abandoned checkout and cart, post-purchase, win-back"
    }, /*#__PURE__*/React.createElement("p", {
      className: "lead",
      style: {
        margin: 0
      }
    }, "Build what it cannot run under ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Email/journeys")
    }, "Journeys"), ": wholesale reorders, browse follow-ups with recommendations, journeys on your lifecycle and behavior data. Use ", /*#__PURE__*/React.createElement("b", null, "New automation"), " only for a one-off triggered email.")) : null, !automations.length && !hideStarters ? /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Empty, null, "No automations yet: create one to reach contacts after the events that matter.")) : null));
  }
}
export default withRouter(EmailAutomationViewComponent);